import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  MapPin,
  Star,
  ChevronRight,
  Ticket,
  Phone,
  ChevronDown,
  Tag,
  Landmark,
  Search,
  Copy,
  ShoppingBag,
} from "lucide-react";
import {
  formatTime,
  formatPrice,
  groupWorkingHoursByDay,
  toSentenceCase,
  getCurrentOpenStatus,
  getBusinessCurrency,
  sortAndCapPopularServices,
  getBookingCartCount,
  isProductListedOnBookingSite,
  isBelowMinNgnCheckoutAmount,
  getSelectedProductsGoodsTotal,
  getMinNgnCheckoutToastMessage,
} from "@/lib/utils";
import { bookingSiteOrigin } from "@/lib/siteUrl";
import { BookingStep, Bundle, Promo, Service } from "@/types/booking";
import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
  type RefObject,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import VoucherRedemptionModal from "../VoucherRedemptionModal";
import { usePreserveQueryParams } from "@/hooks/usePreserveQueryParams";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import VoucherCard from "@/components/ui/voucher_card";
import CartSummary from "../CartSummary";
import CartDialog from "../CartDialog";
import ServiceTile from "../ServiceTile";
import BundleTile from "../BundleTile";
import ProductTile from "../ProductTile";
import {
  generateLocationKeywords,
  generateSeoDescription,
  generateSeoTitle,
} from "@/lib/seoUtils";
import {
  buildBusinessOpenGraph,
  buildHomeOpenGraph,
  resolveShareBundle,
  resolveShareProduct,
  resolveShareService,
} from "@/lib/openGraph";
import OpenGraphTags from "@/components/seo/OpenGraphTags";
import ImageCarousel from "@/components/ui/ImageCarousel";
import { useImageLoader } from "@/hooks/useImageLoader";
import ServicesLoadingSpinner from "../ServicesLoadingSpinner";
import {
  dateOnlyFromInstant,
  differenceInDateOnlyDays,
  formatDateOnlyForDisplay,
  isValidTimeZone,
  parseDateOnly,
  weekdayForDateOnly,
} from "@/lib/salon-time";

const ALL_SERVICES_CATEGORY = "__all__";
const ALL_PRODUCTS_CATEGORY = "__all__";

export default function LandingPage() {
  type CombinedCategory = {
    name: string;
    category: string;
    description?: string | null;
    services: Service[];
    bundles: Bundle[];
    /** From Online Service Display (`categories.order_index`); lower comes first. */
    orderIndex?: number;
  };

  const [searchParams] = useSearchParams();
  const deepLinkedServiceId = searchParams.get("service");
  const deepLinkedProductId = searchParams.get("product");
  const deepLinkedBundleId = searchParams.get("bundle");
  useScrollToTop(
    !deepLinkedServiceId && !deepLinkedProductId && !deepLinkedBundleId,
  );
  const { navigateWithParams } = usePreserveQueryParams();
  const {
    businessInfo,
    salons,
    selectedSalon,
    formattedServices,
    popularServices,
    bundles,
    selectedBundles,
    formattedPackages,
    selectedServices,
    selectedPackages,
    products,
    selectedProducts,
    giftCardCart,
    selectService,
    selectServiceWithVariant,
    selectBundle,
    modifyPackageInCart,
    modifyProductInCart,
    setGiftCardCart,
    updateGiftCardCartQuantity,
    clearGiftCardCart,
    handleNextStep,
    ensureMeGuest,
    activeGuestId,
    guests,
    guestLabel,
    setActiveGuestId,
    isMobile,
    setOpenVoucherRedemptionModal,
    reviews,
    promos,
    setPromoCode,
    addToast,
    faqs,
    loadingServices,
  } = useBooking();

  const routerLocation = useLocation();
  const shouldPromptLocationSelection = Boolean(
    (routerLocation.state as { promptLocationSelection?: boolean } | null)
      ?.promptLocationSelection,
  );
  const hasPromptedLocationSelection = useRef(false);
  const scrolledToDeepLinkServiceId = useRef<string | null>(null);
  const scrolledToDeepLinkProductId = useRef<string | null>(null);
  const scrolledToDeepLinkBundleId = useRef<string | null>(null);
  const [highlightedServiceId, setHighlightedServiceId] = useState<
    string | null
  >(null);
  const [highlightedProductId, setHighlightedProductId] = useState<
    string | null
  >(null);
  const [highlightedBundleId, setHighlightedBundleId] = useState<string | null>(
    null,
  );
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    ALL_SERVICES_CATEGORY,
  );
  const [expandedFaqs, setExpandedFaqs] = useState<string[]>([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [activeSection, setActiveSection] = useState<
    | "services"
    | "packages"
    | "vouchers"
    | "products"
    | "giftcards"
    | "promos"
    | "team"
    | "reviews"
    | "about"
    | "faqs"
  >("services");
  const [servicesSearchQuery, setServicesSearchQuery] = useState("");
  const [selectedProductCategory, setSelectedProductCategory] = useState(
    ALL_PRODUCTS_CATEGORY,
  );
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSalonDialogOpen, setIsSalonDialogOpen] = useState(false);

  // Auto-open the location dialog when a visitor landed on the business URL
  // without choosing a branch and without a shared service or product link.
  useEffect(() => {
    if (!shouldPromptLocationSelection) return;
    if (hasPromptedLocationSelection.current) return;
    if (!salons || salons.length <= 1) return;

    hasPromptedLocationSelection.current = true;
    setIsSalonDialogOpen(true);
  }, [shouldPromptLocationSelection, salons]);
  const headerRef = useRef<HTMLDivElement>(null);
  const categoryPillsBarRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [bookingHeaderHeight, setBookingHeaderHeight] = useState(0);
  const [categoryPillsBarHeight, setCategoryPillsBarHeight] = useState(0);
  const cartCount = getBookingCartCount({
    selectedServices,
    selectedPackages,
    giftCardCart,
    selectedProducts,
  });

  const isVoucherPurchase = selectedPackages.length > 0;
  const isGiftCardPurchase = Boolean(giftCardCart);
  const isProductOnlyPurchase =
    selectedProducts.length > 0 &&
    selectedServices.length === 0 &&
    !isVoucherPurchase &&
    !isGiftCardPurchase;

  const getReviewReplies = (review: any) => {
    const threadedReplies = Array.isArray(review.replies)
      ? review.replies.filter((reply: any) => reply?.body?.trim())
      : [];

    if (threadedReplies.length > 0) {
      return threadedReplies;
    }

    const legacyReply = (review.reply || review.response || "")
      .toString()
      .trim();

    if (!legacyReply) {
      return [];
    }

    return [
      {
        id: `${review.id}-reply`,
        author_type: "business",
        body: legacyReply,
        created_at: review.replied_at || review.updated_at,
      },
    ];
  };

  const getReplyAuthorLabel = (reply: any) => {
    if (reply.author_type === "business") {
      return businessInfo?.name || "Business";
    }
    if (reply.author_type === "client") return "Client follow-up";

    return "Reply";
  };

  const giftCardSetting = businessInfo?.giftCardSettingForBooking;
  const showGiftcards = Boolean(
    giftCardSetting?.enabled && giftCardSetting?.online !== false,
  );
  const listedProducts = useMemo(
    () =>
      (products || []).filter((product) =>
        isProductListedOnBookingSite(product),
      ),
    [products],
  );
  const showProducts = Boolean(
    listedProducts.length > 0 &&
    businessInfo?.productStorefrontEnabled &&
    (businessInfo?.listProductsOnBookingSite || deepLinkedProductId),
  );
  const productCategories = useMemo(() => {
    const names = new Map<string, string>();
    listedProducts.forEach((product) => {
      const name = product.category?.name?.trim();
      if (name) names.set(name.toLowerCase(), name);
    });
    return Array.from(names.values()).sort((a, b) => a.localeCompare(b));
  }, [listedProducts]);
  const visibleProducts = useMemo(() => {
    if (selectedProductCategory === ALL_PRODUCTS_CATEGORY) {
      return listedProducts;
    }
    return listedProducts.filter(
      (product) => product.category?.name === selectedProductCategory,
    );
  }, [listedProducts, selectedProductCategory]);

  const isBookingLinkActive = Boolean(selectedSalon?.bookingLink);
  const reviewList = reviews ?? [];
  const hasReviews = reviewList.length > 0;
  const averageReviewRating = hasReviews
    ? reviewList.reduce(
        (acc: number, r: any) => acc + (r.stars ?? r.rating ?? 0),
        0,
      ) / reviewList.length
    : 0;

  const INITIAL_REVIEWS_SHOWN = 10;
  const REVIEWS_PER_PAGE = 10;

  const sortedReviewList = useMemo(
    () =>
      [...reviewList].sort((a: any, b: any) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      }),
    [reviewList],
  );

  const hasMoreReviewsThanInitial =
    sortedReviewList.length > INITIAL_REVIEWS_SHOWN;
  const totalReviewPages = Math.ceil(
    sortedReviewList.length / REVIEWS_PER_PAGE,
  );

  const displayedReviews = useMemo(() => {
    if (!showAllReviews) {
      return sortedReviewList.slice(0, INITIAL_REVIEWS_SHOWN);
    }

    const startIndex = (reviewsPage - 1) * REVIEWS_PER_PAGE;
    return sortedReviewList.slice(startIndex, startIndex + REVIEWS_PER_PAGE);
  }, [sortedReviewList, showAllReviews, reviewsPage]);

  const currency = getBusinessCurrency(businessInfo);
  const currencySymbol = currency.symbol;
  const currencyStep = currency.factor > 1 ? 1 / currency.factor : 1;
  const giftCardMinAmount = giftCardSetting?.minAmount ?? 0;
  const giftCardMaxAmount = giftCardSetting?.maxAmount ?? 0;

  const tryStartProductCheckout = () => {
    const goodsTotal = getSelectedProductsGoodsTotal(selectedProducts);
    if (isBelowMinNgnCheckoutAmount(goodsTotal, currency)) {
      addToast({
        message: getMinNgnCheckoutToastMessage(currency),
        variant: "error",
      });
      return;
    }
    handleNextStep(BookingStep.PRODUCT_CHECKOUT);
  };

  const [giftCardAmountDraft, setGiftCardAmountDraft] = useState<string>("");
  const [giftCardMessageDraft, setGiftCardMessageDraft] = useState<string>("");
  const [giftCardQuantityDraft, setGiftCardQuantityDraft] = useState<number>(1);

  useEffect(() => {
    if (!giftCardCart) return;
    setGiftCardAmountDraft(String(giftCardCart.amount));
    setGiftCardMessageDraft(giftCardCart.message || "");
    setGiftCardQuantityDraft(giftCardCart.quantity || 1);
  }, [giftCardCart]);

  const handleAddGiftCardToCart = () => {
    if (!selectedSalon?.id) {
      addToast({
        message: "Please select a location to continue.",
        variant: "error",
      });
      return;
    }
    if (!showGiftcards) {
      addToast({
        message: "Gift cards are not available right now.",
        variant: "error",
      });
      return;
    }
    const amount = Number(giftCardAmountDraft);
    if (!Number.isFinite(amount) || amount <= 0) {
      addToast({ message: "Please enter a valid amount.", variant: "error" });
      return;
    }
    if (giftCardMinAmount && amount < giftCardMinAmount) {
      addToast({
        message: `Amount must be at least ${formatPrice(giftCardMinAmount, currency)}`,
        variant: "error",
      });
      return;
    }
    if (giftCardMaxAmount && amount > giftCardMaxAmount) {
      addToast({
        message: `Amount must not exceed ${formatPrice(giftCardMaxAmount, currency)}`,
        variant: "error",
      });
      return;
    }

    if (
      selectedServices.length > 0 ||
      selectedPackages.length > 0 ||
      selectedProducts.length > 0
    ) {
      addToast({
        message: "Please clear your cart before adding a gift card.",
        variant: "error",
      });
      return;
    }

    setGiftCardCart({
      amount,
      message: giftCardMessageDraft,
      quantity: Math.max(1, Math.floor(giftCardQuantityDraft || 1)),
    });
    addToast({ message: "Gift card added to cart.", variant: "success" });
  };

  const limitToWords = (value: string, maxWords: number) => {
    const words = value.trim().split(/\s+/).filter(Boolean);
    if (words.length <= maxWords) return value;
    return words.slice(0, maxWords).join(" ");
  };

  useEffect(() => {
    const vouchersForRenderDebug = formattedPackages.map((pkg) => {
      const isSelected = selectedPackages.some((p) => p.id === pkg.id);
      const priceSource = pkg?.usesCustomPrice ? "customPrice" : "totalPrice";
      const price = pkg?.usesCustomPrice ? pkg?.customPrice : pkg?.totalPrice;

      return {
        id: pkg.id,
        name: pkg.name,
        selected: isSelected,
        isPurchasableAsVoucher: pkg.isPurchasableAsVoucher,
        priceSource,
        price,
        usesCustomPrice: pkg.usesCustomPrice,
        customPrice: pkg.customPrice,
        totalPrice: pkg.totalPrice,
        validityMonths: pkg.validityMonths || 1,
        packageServicesCount: pkg.packageServices?.length || 0,
        packageServices: (pkg.packageServices || []).map((ps: any) => ({
          quantity: ps.quantity,
          serviceName: ps.service?.name || ps.name,
        })),
      };
    });

    console.log("Vouchers (render checks):", {
      hasVouchersSection: formattedPackages.length > 0,
      voucherCount: formattedPackages.length,
      vouchers: vouchersForRenderDebug,
    });
  }, [formattedPackages, selectedPackages]);

  // Validate single header image (carousel handles its own validation)
  const singleHeaderImageUrl =
    businessInfo?.headerImageUrls?.length === 1
      ? businessInfo.headerImageUrls[0].imageUrl
      : null;
  const singleHeaderImageValid = useImageLoader(singleHeaderImageUrl);

  // Validate logo for SEO meta tags
  const logoValid = useImageLoader(businessInfo?.logoUrl);

  const orderedPopularServices = useMemo(() => {
    return sortAndCapPopularServices(popularServices);
  }, [popularServices]);

  // Filter services based on search query
  const filteredServices = formattedServices
    .map((category) => ({
      ...category,
      services: category.services.filter((service) => {
        if (!servicesSearchQuery.trim()) return true;
        const searchTerm = servicesSearchQuery.toLowerCase();
        return (
          service.name.toLowerCase().includes(searchTerm) ||
          category.name.toLowerCase().includes(searchTerm)
        );
      }),
    }))
    .filter((category) => category.services.length > 0); // Only show categories with matching services

  const filteredBundles = (bundles || []).filter((bundle) => {
    if (!servicesSearchQuery.trim()) return true;
    const searchTerm = servicesSearchQuery.toLowerCase();
    return (
      String(bundle?.name || "")
        .toLowerCase()
        .includes(searchTerm) ||
      String(bundle?.category?.name || "")
        .toLowerCase()
        .includes(searchTerm)
    );
  });

  const groupedBundles = filteredBundles.reduce(
    (acc, bundle) => {
      const rawCategoryName = String(bundle?.category?.name || "").trim();
      const key =
        rawCategoryName.length > 0 ? rawCategoryName.toLowerCase() : "other";
      if (!acc[key]) acc[key] = [];
      acc[key].push(bundle);
      return acc;
    },
    {} as Record<string, typeof filteredBundles>,
  );

  const combinedCategories = useMemo(() => {
    const map = new Map<string, CombinedCategory>();

    const orderIndexFromServices = (services: Service[]) => {
      for (const service of services) {
        const index = service.category?.orderIndex;
        if (index !== null && index !== undefined) return index;
      }
      return undefined;
    };

    const descriptionFromServices = (services: Service[]) => {
      for (const service of services) {
        const description = service.category?.description?.trim();
        if (description) return description;
      }
      return null;
    };

    filteredServices.forEach((cat) => {
      const key = String(cat.name || "")
        .trim()
        .toLowerCase();
      const services = cat.services as Service[];
      map.set(key, {
        name: cat.name,
        category: cat.category,
        description:
          cat.description?.trim() || descriptionFromServices(services),
        services,
        bundles: (groupedBundles[key] || []) as Bundle[],
        orderIndex: orderIndexFromServices(services),
      });
    });

    Object.entries(groupedBundles).forEach(
      ([categoryKey, bundlesForCategory]) => {
        if (map.has(categoryKey)) return;
        const displayName =
          String(bundlesForCategory?.[0]?.category?.name || "").trim() ||
          "Other";
        const description =
          bundlesForCategory?.[0]?.category?.description?.trim() || null;
        map.set(categoryKey, {
          name: displayName,
          category: displayName,
          description,
          services: [],
          bundles: bundlesForCategory as Bundle[],
        });
      },
    );

    // Respect Online Service Display order (`orderIndex`). Keep "Other" last;
    // fall back to name only when indexes are equal/missing.
    return Array.from(map.values())
      .filter((cat) => cat.services.length > 0 || cat.bundles.length > 0)
      .sort((a, b) => {
        if (a.name === "Other") return 1;
        if (b.name === "Other") return -1;
        const aOrder = a.orderIndex ?? 999;
        const bOrder = b.orderIndex ?? 999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.name.localeCompare(b.name);
      });
  }, [filteredServices, groupedBundles]);

  const isSearching = servicesSearchQuery.trim().length > 0;
  const isFilteredToCategory =
    !isSearching && selectedCategoryId !== ALL_SERVICES_CATEGORY;

  const visibleCategories = useMemo(() => {
    if (!isFilteredToCategory) return combinedCategories;
    return combinedCategories.filter(
      (category) => category.name === selectedCategoryId,
    );
  }, [combinedCategories, isFilteredToCategory, selectedCategoryId]);

  const handleSelectCategory = (id: string) => {
    setSelectedCategoryId(id);
    if (id !== ALL_SERVICES_CATEGORY) {
      setExpandedCategories((prev) =>
        prev.includes(id) ? prev : [...prev, id],
      );
    }
  };

  // When searching, auto-expand matching categories so bundle-only results are visible.
  useEffect(() => {
    if (!servicesSearchQuery.trim()) return;
    setExpandedCategories(combinedCategories.map((c) => c.name));
  }, [servicesSearchQuery, combinedCategories]);

  const bundleSelectedServiceIds = useMemo(() => {
    return new Set(
      selectedServices.filter((s) => Boolean(s.bundleId)).map((s) => s.id),
    );
  }, [selectedServices]);

  /** Catalog selection is scoped to the guest currently being shopped for. */
  const catalogSelectedServices = useMemo(() => {
    if (!businessInfo?.allowGroupBooking) return selectedServices;

    if (activeGuestId) {
      return selectedServices.filter((s) => s.guestId === activeGuestId);
    }

    const me = guests.find((g) => g.position === 1);
    if (me) {
      return selectedServices.filter(
        (s) => s.guestId === me.id || s.guestId == null,
      );
    }
    return selectedServices.filter((s) => s.guestId == null);
  }, [
    selectedServices,
    businessInfo?.allowGroupBooking,
    activeGuestId,
    guests,
  ]);

  const nonBundleSelectedServices = useMemo(() => {
    return catalogSelectedServices.filter((s) => !s.bundleId);
  }, [catalogSelectedServices]);

  const activeGuest = useMemo(
    () => guests.find((g) => g.id === activeGuestId) ?? null,
    [guests, activeGuestId],
  );

  /** Banner while shopping for a specific guest (hidden on solo Me catalog). */
  const selectingForLabel = useMemo(() => {
    if (!businessInfo?.allowGroupBooking || !activeGuest) return null;
    const hasAdditionalGuests = guests.some((g) => g.position > 1);
    if (activeGuest.position === 1 && !hasAdditionalGuests) return null;
    return guestLabel(activeGuest);
  }, [businessInfo?.allowGroupBooking, activeGuest, guests, guestLabel]);

  const handleSelectService = (serviceId: string) => {
    if (bundleSelectedServiceIds.has(serviceId)) {
      addToast({
        message: "This service is already included in a selected bundle.",
        variant: "warning",
      });
      return;
    }
    selectService(serviceId);
  };

  // Drop the deep-link highlight once that service (or a variant) is in the cart.
  useEffect(() => {
    if (!highlightedServiceId) return;

    const isHighlightedServiceInCart = selectedServices.some((service) => {
      if (service.bundleId) return false;
      return (
        service.id === highlightedServiceId ||
        service.id.startsWith(`${highlightedServiceId}-`)
      );
    });

    if (isHighlightedServiceInCart) {
      setHighlightedServiceId(null);
    }
  }, [selectedServices, highlightedServiceId]);

  useEffect(() => {
    if (!highlightedProductId) return;
    const isHighlightedProductInCart = selectedProducts.some(
      (product) => product.id === highlightedProductId,
    );
    if (isHighlightedProductInCart) {
      setHighlightedProductId(null);
    }
  }, [selectedProducts, highlightedProductId]);

  useEffect(() => {
    if (!highlightedBundleId) return;
    if (selectedBundles.some((bundle) => bundle.id === highlightedBundleId)) {
      setHighlightedBundleId(null);
    }
  }, [selectedBundles, highlightedBundleId]);

  // SEO data
  const allServices: Service[] = formattedServices.flatMap(
    (group) => group.services,
  );

  const seoTitle =
    businessInfo?.name && allServices.length > 0
      ? generateSeoTitle(
          businessInfo.name,
          allServices,
          selectedSalon,
          businessInfo?.businessSpecialties,
        )
      : businessInfo?.name
        ? `Book an appointment at ${businessInfo.name}`
        : "Book an appointment";

  // Also set document title directly as a fallback
  useEffect(() => {
    if (deepLinkedServiceId || deepLinkedProductId || deepLinkedBundleId) return;
    if (seoTitle && seoTitle !== "Book an appointment") {
      document.title = seoTitle;
    }
  }, [seoTitle, deepLinkedServiceId, deepLinkedProductId, deepLinkedBundleId]);

  const seoDescription = businessInfo?.name
    ? generateSeoDescription(
        businessInfo.name,
        businessInfo.about,
        formattedServices,
        selectedSalon,
      )
    : "Book your appointment online. Easy online booking for professional services.";

  const seoKeywords =
    formattedServices.length > 0
      ? generateLocationKeywords(formattedServices, selectedSalon).join(", ")
      : "";

  const origin = bookingSiteOrigin();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";
  const matchedDeepLinkService = resolveShareService(
    allServices,
    deepLinkedServiceId,
  );
  const matchedDeepLinkProduct = resolveShareProduct(
    listedProducts,
    deepLinkedProductId,
  );
  const matchedDeepLinkBundle = resolveShareBundle(bundles, deepLinkedBundleId);
  const openGraph = businessInfo
    ? {
        ...buildBusinessOpenGraph({
          origin,
          pathname,
          businessName: businessInfo.name,
          about: businessInfo.about,
          locationLabel:
            [
              selectedSalon?.branchName,
              selectedSalon?.city,
              selectedSalon?.state,
            ]
              .filter(Boolean)
              .join(", ") || selectedSalon?.address,
          specialties: businessInfo.businessSpecialties,
          headerImages: businessInfo.headerImageUrls?.map(
            (image) => image.imageUrl,
          ),
          headerImageUrl: businessInfo.headerImageUrl,
          logoUrl: businessInfo.logoUrl,
          serviceName: matchedDeepLinkService?.name,
          serviceDescription: matchedDeepLinkService?.description,
          serviceId: matchedDeepLinkService?.id,
          serviceDuration: matchedDeepLinkService?.duration,
          servicePrice: matchedDeepLinkService?.price,
          servicePricingType: matchedDeepLinkService?.pricingType,
          servicePriceFrom: matchedDeepLinkService?.priceFrom,
          productName: matchedDeepLinkProduct?.name,
          productDescription: matchedDeepLinkProduct?.description,
          productId: matchedDeepLinkProduct?.id,
          productPrice: matchedDeepLinkProduct?.price,
          productImageUrl: matchedDeepLinkProduct?.imageUrl,
          bundleName: matchedDeepLinkBundle?.name,
          bundleDescription: matchedDeepLinkBundle?.description,
          bundleId: matchedDeepLinkBundle?.id,
          bundlePrice: matchedDeepLinkBundle?.price,
          bundleDuration: matchedDeepLinkBundle?.duration,
          currencySymbol: currency.symbol,
          priceHidden: Boolean(
            (businessInfo as { priceHidden?: boolean }).priceHidden,
          ),
        }),
        ...(matchedDeepLinkService ||
        matchedDeepLinkProduct ||
        matchedDeepLinkBundle
          ? {}
          : {
              documentTitle: seoTitle,
              searchDescription: seoDescription,
            }),
      }
    : buildHomeOpenGraph(origin);

  const structuredData = businessInfo
    ? {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: businessInfo.name,
        description: businessInfo.about,
        url: openGraph.url,
        ...(businessInfo.logoUrl && { logo: businessInfo.logoUrl }),
        ...(selectedSalon && {
          address: {
            "@type": "PostalAddress",
            streetAddress: selectedSalon.address,
            addressLocality: selectedSalon.city,
            addressRegion: selectedSalon.state,
          },
        }),
        ...(selectedSalon?.phone && {
          telephone: `${selectedSalon.callingCode}${selectedSalon.phone}`,
        }),
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Services",
          itemListElement: formattedServices.slice(0, 10).map((service) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: service.name,
              description: "",
            },
          })),
        },
      }
    : null;

  // Auto-expand categories that contain selected services
  useEffect(() => {
    if (formattedServices.length > 0 && selectedServices.length > 0) {
      const categoriesToExpand = formattedServices
        .filter((category) =>
          category.services.some((service) =>
            selectedServices.some((selected) => selected.id === service.id),
          ),
        )
        .map((category) => category.name);

      if (categoriesToExpand.length > 0) {
        setExpandedCategories((prev) => {
          const newExpanded = [...new Set([...prev, ...categoriesToExpand])];
          return newExpanded;
        });
      }
    }
  }, [formattedServices, selectedServices]);

  useEffect(() => {
    setShowAllReviews(false);
    setReviewsPage(1);
  }, [reviews, selectedSalon?.id]);

  useEffect(() => {
    setSelectedCategoryId(ALL_SERVICES_CATEGORY);
    setSelectedProductCategory(ALL_PRODUCTS_CATEGORY);
  }, [selectedSalon?.id]);

  useEffect(() => {
    if (isSearching) return;
    if (selectedCategoryId === ALL_SERVICES_CATEGORY) return;
    if (
      !combinedCategories.some(
        (category) => category.name === selectedCategoryId,
      )
    ) {
      setSelectedCategoryId(ALL_SERVICES_CATEGORY);
    }
  }, [combinedCategories, selectedCategoryId, isSearching]);

  // Calculate header height for sticky positioning
  useEffect(() => {
    const updateHeaderHeight = () => {
      const bookingHeader = document.getElementById("booking-site-header");
      setBookingHeaderHeight(bookingHeader?.offsetHeight ?? 0);
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
      setCategoryPillsBarHeight(categoryPillsBarRef.current?.offsetHeight ?? 0);
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, [
    businessInfo,
    reviews,
    selectedSalon,
    isSearching,
    combinedCategories.length,
    loadingServices,
  ]);

  const stickyChromeHeight = bookingHeaderHeight + headerHeight;
  const servicesStickyOffset = stickyChromeHeight + categoryPillsBarHeight;

  // Smooth scroll to section functionality
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element && headerRef.current) {
      const offset = stickyChromeHeight + 16;
      const elementPosition = element.offsetTop - offset;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  // Scroll spy functionality to detect active section
  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return;

      const scrollPosition = window.scrollY + stickyChromeHeight + 100;

      const sections = [
        "services",
        ...(showProducts ? ["products"] : []),
        "vouchers",
        ...(showGiftcards ? ["giftcards"] : []),
        "promos",
        "reviews",
        "about",
        ...(faqs && faqs.length > 0 ? ["faqs"] : []),
      ];
      let activeSection = "services"; // Default to services

      // Find the section that's currently most in view
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;

          // Check if the section is in view with threshold
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            activeSection = sectionId;
            break;
          }

          // If we're past all sections, set the last one as active
          if (scrollPosition >= elementTop) {
            activeSection = sectionId;
          }
        }
      }

      setActiveSection(activeSection as any);
    };

    // Throttle scroll events for better performance
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const throttledHandleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 50); // 50ms throttle
    };

    window.addEventListener("scroll", throttledHandleScroll);
    handleScroll(); // Check initial position

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [stickyChromeHeight, faqs, showGiftcards, showProducts]);

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((c) => c !== categoryName)
        : [...prev, categoryName],
    );
  };

  useEffect(() => {
    if (!deepLinkedServiceId) {
      scrolledToDeepLinkServiceId.current = null;
      return;
    }
    if (loadingServices) return;
    if (scrolledToDeepLinkServiceId.current === deepLinkedServiceId) return;

    const matchingGroup = formattedServices.find((category) =>
      category.services.some((service) => service.id === deepLinkedServiceId),
    );
    const matchedService = matchingGroup?.services.find(
      (service) => service.id === deepLinkedServiceId,
    );

    if (!matchedService || !matchingGroup) {
      return;
    }

    setActiveSection("services");
    setExpandedCategories((prev) =>
      prev.includes(matchingGroup.name) ? prev : [...prev, matchingGroup.name],
    );

    const startedAt = Date.now();
    const targetId = matchedService.id;

    const tryApplyHighlight = () => {
      const el = document.getElementById(`service-${targetId}`);
      if (!el) {
        if (Date.now() - startedAt < 15000) {
          window.setTimeout(tryApplyHighlight, 50);
        }
        return;
      }

      scrolledToDeepLinkServiceId.current = targetId;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedServiceId(targetId);
    };

    window.setTimeout(tryApplyHighlight, 50);
  }, [deepLinkedServiceId, loadingServices, formattedServices]);

  useEffect(() => {
    if (!deepLinkedBundleId) {
      scrolledToDeepLinkBundleId.current = null;
      return;
    }
    if (loadingServices) return;
    if (scrolledToDeepLinkBundleId.current === deepLinkedBundleId) return;

    const matchingGroup = combinedCategories.find((category) =>
      category.bundles.some((bundle) => bundle.id === deepLinkedBundleId),
    );
    const matchedBundle = matchingGroup?.bundles.find(
      (bundle) => bundle.id === deepLinkedBundleId,
    );

    if (!matchedBundle || !matchingGroup) {
      return;
    }

    setActiveSection("services");
    setExpandedCategories((prev) =>
      prev.includes(matchingGroup.name) ? prev : [...prev, matchingGroup.name],
    );

    const startedAt = Date.now();
    const targetId = matchedBundle.id;

    const tryApplyHighlight = () => {
      const el = document.getElementById(`bundle-${targetId}`);
      if (!el) {
        if (Date.now() - startedAt < 15000) {
          window.setTimeout(tryApplyHighlight, 50);
        }
        return;
      }

      scrolledToDeepLinkBundleId.current = targetId;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedBundleId(targetId);
    };

    window.setTimeout(tryApplyHighlight, 50);
  }, [deepLinkedBundleId, loadingServices, combinedCategories]);

  useEffect(() => {
    if (!deepLinkedProductId) {
      scrolledToDeepLinkProductId.current = null;
      return;
    }
    if (loadingServices) return;
    if (!showProducts) return;
    if (
      typeof document !== "undefined" &&
      document.getElementById("booking-site-header") &&
      bookingHeaderHeight === 0
    ) {
      return;
    }
    if (scrolledToDeepLinkProductId.current === deepLinkedProductId) return;

    const matchedProduct = listedProducts.find(
      (product) => product.id === deepLinkedProductId,
    );
    if (!matchedProduct) return;

    setActiveSection("products");
    setSelectedProductCategory(ALL_PRODUCTS_CATEGORY);

    const startedAt = Date.now();
    const targetId = matchedProduct.id;
    let cancelled = false;
    let retryId: number | undefined;

    const tryApplyHighlight = () => {
      if (cancelled) return;
      const el = document.getElementById(`product-${targetId}`);
      if (!el) {
        if (Date.now() - startedAt < 15000) {
          retryId = window.setTimeout(tryApplyHighlight, 50);
        }
        return;
      }

      scrolledToDeepLinkProductId.current = targetId;
      const top =
        el.getBoundingClientRect().top +
        window.scrollY -
        (stickyChromeHeight + 16);
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      setHighlightedProductId(targetId);
    };

    retryId = window.setTimeout(tryApplyHighlight, 50);
    return () => {
      cancelled = true;
      if (retryId) window.clearTimeout(retryId);
    };
  }, [
    deepLinkedProductId,
    listedProducts,
    loadingServices,
    showProducts,
    bookingHeaderHeight,
    stickyChromeHeight,
  ]);

  const toggleFaq = (faqId: string) => {
    setExpandedFaqs((prev) =>
      prev.includes(faqId)
        ? prev.filter((id) => id !== faqId)
        : [...prev, faqId],
    );
  };

  return (
    <>
      <OpenGraphTags {...openGraph} />
      <Helmet>
        {seoKeywords && <meta name="keywords" content={seoKeywords} />}

        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>

      <div className="bg-grey-10 overflow-x-clip max-w-6xl mx-auto py-8 pb-4">
        {businessInfo?.headerImageUrls &&
          businessInfo.headerImageUrls.length > 0 && (
            <div className="relative">
              {businessInfo.headerImageUrls.length === 1 ? (
                // Single image - only show if it loads successfully
                singleHeaderImageValid && (
                  <div className="relative h-64 md:h-80 lg:h-96 w-full overflow-hidden">
                    <img
                      src={businessInfo.headerImageUrls[0].imageUrl}
                      alt={businessInfo.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay with business name on mobile */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:hidden">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h1 className="text-2xl font-bold text-white">
                          {businessInfo?.name}
                        </h1>
                        <p className="text-white/90 text-sm mt-1">
                          {selectedSalon?.address}
                          {selectedSalon?.city && `, ${selectedSalon.city}`}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                // Multiple images - ImageCarousel handles its own validation
                <ImageCarousel
                  images={businessInfo.headerImageUrls}
                  businessName={businessInfo.name}
                  selectedSalon={selectedSalon || undefined}
                />
              )}
            </div>
          )}

        {/* Business info - not sticky */}
        <div className="bg-white">
          <div className="max-w-6xl mx-auto px-4 py-6">
            {/* Business Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <h1
                  className={`text-3xl lg:text-4xl font-semibold text-grey-900 mb-3 tracking-tight font-heading ${
                    (businessInfo?.headerImageUrls?.length === 1 &&
                      singleHeaderImageValid) ||
                    (businessInfo?.headerImageUrls &&
                      businessInfo.headerImageUrls.length > 1)
                      ? "hidden lg:block"
                      : ""
                  }`}
                >
                  {businessInfo?.name}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {/* Rating */}
                  {isBookingLinkActive && hasReviews && (
                    <div className="flex items-center gap-1.5">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold text-grey-900">
                        {averageReviewRating.toFixed(1)}
                      </span>
                      <span className="text-grey-500 whitespace-nowrap">
                        ({reviewList.length} review
                        {reviewList.length !== 1 ? "s" : ""})
                      </span>
                    </div>
                  )}

                  {/* Location with Salon Switcher */}
                  {salons && salons.length > 1 ? (
                    <>
                      <button
                        onClick={() => setIsSalonDialogOpen(true)}
                        className="flex items-center gap-1.5 bg-grey-50 px-2 py-1 rounded-lg transition-colors hover:bg-grey-100 max-w-full"
                      >
                        <MapPin className="h-4 w-4 text-grey-500 shrink-0" />
                        <span className="text-grey-700 truncate flex-1 min-w-0">
                          {selectedSalon?.branchName ||
                            selectedSalon?.address ||
                            "Select a salon"}
                          {selectedSalon?.city && `, ${selectedSalon.city}`}
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 text-grey-500 ml-1 shrink-0" />
                      </button>

                      <Dialog
                        open={isSalonDialogOpen}
                        onOpenChange={setIsSalonDialogOpen}
                      >
                        <DialogContent className="max-w-md w-full bg-white">
                          <DialogHeader>
                            <DialogTitle className="text-lg font-semibold text-grey-900">
                              Select a location
                            </DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col gap-2 max-h-96 overflow-y-auto w-full">
                            {salons.map((salon) => (
                              <button
                                key={salon.id}
                                className={`w-full text-left px-4 py-3 rounded-lg border transition-colors hover:bg-grey-50 ${
                                  selectedSalon?.id === salon.id
                                    ? "bg-green-300/10 border-green-300"
                                    : "border-grey-100"
                                }`}
                                style={{ minWidth: 0 }}
                                onClick={() => {
                                  // Navigate to the new salon URL
                                  const businessUrl =
                                    businessInfo?.bookingUrlIdentifier ||
                                    businessInfo?.urlIdentifier;
                                  const salonUrl =
                                    salon.urlIdentifier || salon.id;
                                  setIsSalonDialogOpen(false);
                                  navigateWithParams(
                                    `/${businessUrl}/${salonUrl}`,
                                  );
                                }}
                              >
                                <div className="flex items-start gap-2 w-full min-w-0">
                                  <Landmark className="h-5 w-5 text-green-300 flex-shrink-0" />
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    <div className="font-medium text-grey-900 truncate">
                                      {salon.branchName || salon.address}
                                    </div>
                                    <div className="text-sm text-grey-600 truncate">
                                      {salon.address}
                                      {salon.city && `, ${salon.city}`}
                                    </div>
                                    {(() => {
                                      // Vouchers are business-wide: count unique voucher packages across ALL salons
                                      const allVouchers = salons
                                        .flatMap((s) => s.packages || [])
                                        .filter(
                                          (pkg, index, self) =>
                                            pkg.isPurchasableAsVoucher ===
                                              true &&
                                            self.findIndex(
                                              (p) => p.id === pkg.id,
                                            ) === index,
                                        );
                                      const voucherCount = allVouchers.length;
                                      return voucherCount > 0 ? (
                                        <div className="text-xs text-green-300 mt-1 truncate">
                                          {voucherCount} voucher
                                          {voucherCount > 1 ? "s" : ""}{" "}
                                          available
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5 max-w-full">
                      <MapPin className="h-4 w-4 text-grey-500 shrink-0" />
                      <span className="text-grey-700 truncate flex-1 min-w-0">
                        {selectedSalon?.address}
                        {selectedSalon?.city && `, ${selectedSalon.city}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {promos &&
                promos.length > 0 &&
                businessInfo?.showPromosOnBookingSite && (
                  <div className="flex items-center gap-3">
                    {promos.map((promo, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-green-300/10 px-3 py-1.5 rounded-md"
                      >
                        <Tag className="h-4 w-4 text-green-300/50" />
                        <span className="text-green-300 text-sm font-medium">
                          {promo.title}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(promo.title);
                            addToast({
                              message: "Copied to clipboard",
                              variant: "success",
                            });
                          }}
                          className="ml-1 p-1 hover:bg-green-100 rounded-md transition-colors"
                        >
                          <Copy className="h-3.5 w-3.5 text-green-300/50" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenVoucherRedemptionModal(true)}
                  className="flex items-center gap-2"
                >
                  <Ticket className="h-4 w-4" />
                  <span>Redeem voucher</span>
                </Button>
              </div>
            </div>
            {/* Status */}
            {/* Status */}
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-1.5">
                {(() => {
                  const salonTimezone = selectedSalon?.timezone;
                  if (!isValidTimeZone(salonTimezone)) {
                    return (
                      <>
                        <div className="w-2 h-2 bg-red-500 shrink-0 rounded-full"></div>
                        <p className="text-grey-700 font-medium">Closed</p>
                      </>
                    );
                  }
                  const { isOpen, todayHours } = getCurrentOpenStatus(
                    selectedSalon?.salonHours || [],
                    salonTimezone,
                    selectedSalon?.closedPeriods || [],
                  );

                  if (isOpen && todayHours && todayHours.length > 0) {
                    return (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-grey-700 font-medium">
                          Open -{" "}
                          {todayHours
                            .sort((a, b) => {
                              const aTime = a.openTime.split(":").map(Number);
                              const bTime = b.openTime.split(":").map(Number);
                              const aMinutes = aTime[0] * 60 + aTime[1];
                              const bMinutes = bTime[0] * 60 + bTime[1];
                              return aMinutes - bMinutes;
                            })
                            .map((slot, index) => (
                              <span key={index}>
                                {formatTime(slot.openTime)} to{" "}
                                {formatTime(slot.closeTime)}
                                {index < todayHours.length - 1 ? ", " : ""}
                              </span>
                            ))}
                        </span>
                      </>
                    );
                  } else {
                    const { nextOpenTime } = getCurrentOpenStatus(
                      selectedSalon?.salonHours || [],
                      salonTimezone,
                      selectedSalon?.closedPeriods || [],
                    );
                    const now = new Date();
                    const today = parseDateOnly(
                      dateOnlyFromInstant(now, salonTimezone),
                    )!;
                    const currentDay = weekdayForDateOnly(today).toLowerCase();

                    // Check if next open time is within 7 days
                    const getNextOpenDisplay = () => {
                      if (!nextOpenTime) return null;

                      const nextOpenDate = nextOpenTime.date || null;
                      const daysDifference = nextOpenDate
                        ? differenceInDateOnlyDays(nextOpenDate, today)
                        : 0;

                      // Check if it's actually today (same day name AND within 1 day)
                      const isToday =
                        nextOpenTime.day === currentDay && daysDifference <= 1;

                      if (isToday) {
                        return formatTime(nextOpenTime.time);
                      } else if (daysDifference <= 7) {
                        // Within 7 days - show day name
                        const dayName =
                          nextOpenTime.day.charAt(0).toUpperCase() +
                          nextOpenTime.day.slice(1);
                        return `${formatTime(nextOpenTime.time)} (${dayName})`;
                      } else {
                        // Beyond 7 days - show date
                        const dateStr = nextOpenDate
                          ? formatDateOnlyForDisplay(nextOpenDate, {
                              month: "short",
                              day: "numeric",
                            })
                          : "";
                        return `${formatTime(nextOpenTime.time)} (${dateStr})`;
                      }
                    };

                    return (
                      <>
                        <div className="w-2 h-2 bg-red-500 shrink-0 rounded-full"></div>
                        <p className="text-grey-700 font-medium">
                          {nextOpenTime ? (
                            <>Closed — Opens at {getNextOpenDisplay()}</>
                          ) : (
                            "Closed"
                          )}
                        </p>
                      </>
                    );
                  }
                })()}
              </div>
            </div>
            {!businessInfo?.acceptsOnlineBookings && (
              <div className="p-3 bg-grey-50 rounded-lg mt-4 mb-4">
                <p className="text-sm text-grey-600">
                  This business does not accept online bookings. You can contact
                  them on{" "}
                  {selectedSalon?.phone && (
                    <a
                      href={`tel:${selectedSalon.callingCode}${selectedSalon.phone}`}
                      className="underline"
                    >
                      {selectedSalon.callingCode}
                      {selectedSalon.phone}
                    </a>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Section tabs - sticky */}
        <div
          ref={headerRef}
          className="sticky z-40 bg-white border-b border-grey-75"
          style={{ top: bookingHeaderHeight }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3">
            <HorizontalFadeScroller className="flex gap-2 overflow-x-scroll scrollbar-hide">
              <NavigationTab
                activeSection={activeSection}
                label="Services"
                onClick={() => {
                  setActiveSection("services");
                  scrollToSection("services");
                }}
              />
              {showProducts && (
                <NavigationTab
                  activeSection={activeSection}
                  label="Products"
                  onClick={() => {
                    setActiveSection("products");
                    setServicesSearchQuery("");
                    scrollToSection("products");
                  }}
                />
              )}
              {formattedPackages.length > 0 && (
                <NavigationTab
                  activeSection={activeSection}
                  label="Vouchers"
                  onClick={() => {
                    setActiveSection("vouchers");
                    setServicesSearchQuery("");
                    scrollToSection("vouchers");
                  }}
                />
              )}

              {showGiftcards && (
                <NavigationTab
                  activeSection={activeSection}
                  label="Giftcards"
                  onClick={() => {
                    setActiveSection("giftcards");
                    setServicesSearchQuery("");
                    scrollToSection("giftcards");
                  }}
                />
              )}

              {promos &&
                promos.length > 0 &&
                businessInfo?.showPromosOnBookingSite && (
                  <NavigationTab
                    activeSection={activeSection}
                    label="Promos"
                    onClick={() => {
                      setActiveSection("promos");
                      setServicesSearchQuery("");
                      scrollToSection("promos");
                    }}
                  />
                )}

              {isBookingLinkActive && (
                <NavigationTab
                  activeSection={activeSection}
                  label="Reviews"
                  onClick={() => {
                    setActiveSection("reviews");
                    setServicesSearchQuery("");
                    scrollToSection("reviews");
                  }}
                />
              )}

              <NavigationTab
                activeSection={activeSection}
                label="About"
                onClick={() => {
                  setActiveSection("about");
                  setServicesSearchQuery("");
                  scrollToSection("about");
                }}
              />
              {faqs && faqs.length > 0 && (
                <NavigationTab
                  activeSection={activeSection}
                  label="FAQs"
                  onClick={() => {
                    setActiveSection("faqs");
                    setServicesSearchQuery("");
                    scrollToSection("faqs");
                  }}
                />
              )}
            </HorizontalFadeScroller>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-6 lg:pb-[500px]">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
              <section
                id="services"
                className="space-y-4"
                style={{ scrollMarginTop: `${servicesStickyOffset + 16}px` }}
              >
                {/* Show loading spinner while services are being fetched */}
                {loadingServices ? (
                  <ServicesLoadingSpinner />
                ) : (
                  <>
                    {/* Search Input for Services */}
                    <div className="">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-5 w-5 text-grey-400" />
                        </div>
                        <input
                          type="text"
                          placeholder="Search services or categories..."
                          value={servicesSearchQuery}
                          onChange={(e) =>
                            setServicesSearchQuery(e.target.value)
                          }
                          className="block w-full pl-10 pr-3 py-3 border placeholder:text-sm border-grey-75 rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-300 outline-none transition-colors bg-white text-grey-900 placeholder-grey-500"
                        />
                      </div>
                    </div>

                    {selectingForLabel && (
                      <div className="flex items-center gap-3 rounded-lg border border-green-100 bg-green-50 px-4 py-3">
                        <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sm font-semibold text-green-600 flex-shrink-0">
                          {selectingForLabel
                            .split(/\s+/)
                            .slice(0, 2)
                            .map((p) => p[0]?.toUpperCase() ?? "")
                            .join("")}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-grey-900">
                            Selecting for {selectingForLabel}
                          </p>
                          <p className="text-xs text-grey-600">
                            Choose services for this person. Your other
                            guests&apos; selections stay in the booking summary.
                          </p>
                        </div>
                      </div>
                    )}

                    {!isSearching && combinedCategories.length > 1 && (
                      <div
                        ref={categoryPillsBarRef}
                        className="sticky z-30 -mx-4 -mt-4 px-4 pt-4 pb-2 bg-grey-10"
                        style={{ top: stickyChromeHeight }}
                      >
                        <ServiceCategoryPills
                          categories={combinedCategories}
                          selectedId={selectedCategoryId}
                          onSelect={handleSelectCategory}
                        />
                      </div>
                    )}

                    {/* Popular Services */}
                    {!isSearching &&
                      !isFilteredToCategory &&
                      businessInfo?.showFeaturedServicesOnBookingSite !==
                        false &&
                      orderedPopularServices.length > 0 && (
                        <>
                          <div className="rounded-lg p-4">
                            <h2 className="text-lg font-semibold text-grey-900 font-heading">
                              Popular services
                            </h2>
                            <p className="text-sm text-grey-600 mt-1">
                              Our most booked treatments
                            </p>
                          </div>

                          {orderedPopularServices.map((service) => (
                            <ServiceTile
                              key={service.id}
                              service={service}
                              businessInfo={businessInfo}
                              selected={catalogSelectedServices.some(
                                (s) => s.id === service.id,
                              )}
                              onClick={() => handleSelectService(service.id)}
                              onSelectVariant={selectServiceWithVariant}
                              allowMultiple={true}
                              selectedServices={catalogSelectedServices}
                              highlighted={highlightedServiceId === service.id}
                            />
                          ))}
                        </>
                      )}

                    {/* All Services by Category */}
                    {combinedCategories.length > 0 ? (
                      visibleCategories.map((category) => {
                        const isExpanded =
                          isFilteredToCategory ||
                          expandedCategories.includes(category.name);
                        const countParts: string[] = [];
                        if (category.services.length > 0) {
                          countParts.push(
                            `${category.services.length} service${
                              category.services.length !== 1 ? "s" : ""
                            }`,
                          );
                        }
                        if (category.bundles.length > 0) {
                          countParts.push(
                            `${category.bundles.length} bundle${
                              category.bundles.length !== 1 ? "s" : ""
                            }`,
                          );
                        }
                        const countLabel = countParts.join(" • ");

                        return (
                          <div
                            key={category.name}
                            className="bg-white rounded-lg border-1 transition-all duration-300 border-grey-75"
                          >
                            <div
                              className={`p-4 ${
                                isExpanded ? "border-b border-grey-100" : ""
                              }`}
                            >
                              <button
                                onClick={() => {
                                  if (isFilteredToCategory) return;
                                  toggleCategory(category.name);
                                }}
                                className={`${
                                  isFilteredToCategory
                                    ? "cursor-default"
                                    : "cursor-pointer"
                                }  w-full flex items-center justify-between group`}
                              >
                                <div className="text-left min-w-0 pr-2">
                                  <h2 className="text-base font-semibold text-grey-900 font-heading capitalize">
                                    {category.name}
                                  </h2>
                                  {category.description ? (
                                    <p
                                      className={`text-sm text-grey-500 mt-1 ${
                                        isExpanded ? "" : "line-clamp-2"
                                      }`}
                                    >
                                      {category.description}
                                    </p>
                                  ) : null}
                                  {countLabel ? (
                                    <p className="text-sm text-grey-600 mt-0.5">
                                      {countLabel}
                                    </p>
                                  ) : null}
                                </div>
                                {!isFilteredToCategory && (
                                  <ChevronRight
                                    className={`h-5 w-5 text-grey-400 transition-transform ${
                                      isExpanded ? "rotate-90" : ""
                                    }`}
                                  />
                                )}
                              </button>
                            </div>

                            {isExpanded && (
                              <div className="space-y-2">
                                {category.bundles.length > 0 && (
                                  <div className="divide-y divide-grey-100">
                                    {category.bundles.map((bundle) => (
                                      <div
                                        key={bundle.id}
                                        id={`bundle-${bundle.id}`}
                                        className={
                                          highlightedBundleId === bundle.id
                                            ? "service-deep-link-glow"
                                            : undefined
                                        }
                                        style={{
                                          scrollMarginTop: `${servicesStickyOffset + 16}px`,
                                        }}
                                      >
                                        <BundleTile
                                          bundle={bundle}
                                          businessInfo={businessInfo}
                                          services={allServices}
                                          selected={selectedBundles.some(
                                            (b) => b.id === bundle.id,
                                          )}
                                          onSelect={() =>
                                            selectBundle(bundle.id)
                                          }
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {category.services.length > 0 && (
                                  <div className="divide-y divide-grey-100">
                                    {category.services.map((service) => (
                                      <div
                                        key={service.id}
                                        id={`service-${service.id}`}
                                        style={{
                                          scrollMarginTop: `${servicesStickyOffset + 16}px`,
                                        }}
                                      >
                                        <ServiceTile
                                          service={service}
                                          businessInfo={businessInfo}
                                          selected={catalogSelectedServices.some(
                                            (s) =>
                                              s.id === service.id &&
                                              !s.bundleId,
                                          )}
                                          onClick={() =>
                                            handleSelectService(service.id)
                                          }
                                          onSelectVariant={
                                            selectServiceWithVariant
                                          }
                                          allowMultiple={true}
                                          selectedServices={
                                            nonBundleSelectedServices
                                          }
                                          highlighted={
                                            highlightedServiceId === service.id
                                          }
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="bg-white rounded-lg p-8 text-center">
                        <Search className="h-12 w-12 text-grey-300 mx-auto mb-3" />
                        <p className="text-grey-600">
                          No services found matching &quot;{servicesSearchQuery}
                          &quot;
                        </p>
                        <p className="text-sm text-grey-500 mt-1">
                          Try a different search term or browse our categories
                        </p>
                      </div>
                    )}
                  </>
                )}
              </section>

              <section
                className="space-y-4"
                id="products"
                style={{ scrollMarginTop: `${stickyChromeHeight + 16}px` }}
              >
                {showProducts && (
                  <>
                    <div className="mb-4 mt-10">
                      <h2 className="text-base font-semibold text-grey-900 mb-1 font-heading">
                        Products
                      </h2>
                      <p className="text-sm text-grey-500">
                        Shop items from this location.
                      </p>
                    </div>
                    {productCategories.length > 1 && (
                      <div
                        className="sticky z-30 -mx-4 px-4 pt-3 pb-2 bg-grey-10"
                        style={{ top: stickyChromeHeight }}
                      >
                        <ServiceCategoryPills
                          categories={productCategories.map((name) => ({
                            name,
                          }))}
                          selectedId={selectedProductCategory}
                          onSelect={setSelectedProductCategory}
                          ariaLabel="Product categories"
                        />
                      </div>
                    )}
                    {visibleProducts.length > 0 ? (
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
                        {visibleProducts.map((product) => (
                          <ProductTile
                            key={product.id}
                            product={product}
                            businessInfo={businessInfo}
                            cartItem={selectedProducts.find(
                              (item) => item.id === product.id,
                            )}
                            highlighted={highlightedProductId === product.id}
                            scrollMarginTop={stickyChromeHeight + 16}
                            onAdd={() => modifyProductInCart(product.id, "add")}
                            onIncrement={() =>
                              modifyProductInCart(product.id, "add")
                            }
                            onDecrement={() =>
                              modifyProductInCart(product.id, "remove")
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg p-8 text-center">
                        <ShoppingBag className="h-12 w-12 text-grey-300 mx-auto mb-3" />
                        <p className="text-grey-600">
                          No products are listed for this location yet.
                        </p>
                        <p className="text-sm text-grey-500 mt-1">
                          For-sale products will show here once they are
                          available at this location.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </section>

              <section
                className="space-y-4"
                id="vouchers"
                style={{ scrollMarginTop: `${headerHeight + 40}px` }}
              >
                {formattedPackages.length > 0 && (
                  <>
                    <div className=" mb-6 mt-10">
                      <h2 className="text-base font-semibold text-grey-900 mb-1 font-heading">
                        Gift Vouchers
                      </h2>
                      <p className="text-sm text-grey-500">
                        Purchase vouchers as gifts or for future use. Valid for
                        the specified duration from purchase date.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {formattedPackages.map((pkg) => (
                        <VoucherCard
                          key={pkg.id}
                          selected={selectedPackages.some(
                            (p) => p.id === pkg.id,
                          )}
                          onPurchase={() => {
                            const isSelected = selectedPackages.some(
                              (p) => p.id === pkg.id,
                            );
                            if (isSelected) {
                              modifyPackageInCart(pkg.id, "remove");
                            } else {
                              modifyPackageInCart(pkg.id, "add");
                            }
                          }}
                          pkg={pkg}
                          businessInfo={businessInfo}
                        />
                      ))}
                    </div>
                  </>
                )}
              </section>

              <section
                className="space-y-4 bg-gradient-to-br from-green-300/20 via-white to-grey-50 p-4 rounded-lg border border-grey-100 mt-2"
                id="giftcards"
                style={{ scrollMarginTop: `${headerHeight + 40}px` }}
              >
                {showGiftcards && (
                  <>
                    <div className=" mb-6 mt-10">
                      <h2 className="text-base font-semibold text-grey-900 mb-1 font-heading">
                        {giftCardSetting?.name || "Giftcards"}
                      </h2>
                      <p className="text-sm text-grey-500">
                        {giftCardSetting?.description ||
                          "Gift cards do not expire. Purchased value is added to recipient wallet and can be used anytime."}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                      <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-grey-900">
                          Customize giftcard
                        </h3>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-grey-700">
                            Amount
                          </label>
                          <div className="flex items-center gap-3">
                            <div className="px-3 py-3 border border-grey-200 rounded-lg bg-grey-50 text-grey-700 font-medium">
                              {currencySymbol}
                            </div>
                            <input
                              type="number"
                              min={giftCardMinAmount || undefined}
                              max={giftCardMaxAmount || undefined}
                              step={currencyStep}
                              value={giftCardAmountDraft}
                              onChange={(e) =>
                                setGiftCardAmountDraft(e.target.value)
                              }
                              placeholder={`${formatPrice(giftCardMinAmount, currency)} - ${formatPrice(giftCardMaxAmount, currency)}`}
                              className="flex-1 border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-grey-700">
                            Add message
                          </label>
                          <textarea
                            value={giftCardMessageDraft}
                            onChange={(e) =>
                              setGiftCardMessageDraft(
                                limitToWords(e.target.value, 15),
                              )
                            }
                            placeholder="Add a message"
                            rows={3}
                            className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300 resize-none"
                          />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                          {giftCardAmountDraft && (
                            <Button
                              type="button"
                              variant="primary"
                              className="sm:flex-1"
                              onClick={handleAddGiftCardToCart}
                            >
                              Add to cart
                            </Button>
                          )}
                          {/* 
	                          <div className="flex items-center justify-between sm:justify-start gap-3">
	                            <button
	                              type="button"
	                              onClick={() =>
	                                setGiftCardQuantityDraft((q) => Math.max(1, q - 1))
	                              }
	                              className="w-10 h-10 rounded-full border border-grey-200 flex items-center justify-center hover:bg-grey-50"
	                            >
	                              –
	                            </button>
	                            <span className="min-w-8 text-center font-medium text-grey-900">
	                              {giftCardQuantityDraft}
	                            </span>
	                            <button
	                              type="button"
	                              onClick={() => setGiftCardQuantityDraft((q) => q + 1)}
	                              className="w-10 h-10 rounded-full border border-grey-200 flex items-center justify-center hover:bg-grey-50"
	                            >
	                              +
	                            </button>
	                          </div> */}
                        </div>

                        {/* {giftCardCart && (
	                          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-100 rounded-lg">
	                            <p className="text-sm text-green-800">
	                              Gift card added to cart.
	                            </p>
	                            <button
	                              type="button"
	                              onClick={() => {
	                                clearGiftCardCart();
	                                setGiftCardAmountDraft("");
	                                setGiftCardMessageDraft("");
	                                setGiftCardQuantityDraft(1);
	                              }}
	                              className="text-sm font-medium text-green-700 hover:text-green-800"
	                            >
	                              Remove
	                            </button>
	                          </div>
	                        )} */}
                      </div>

                      <div className="w-full">
                        <div className="w-full max-w-xl bg-[#0B6B5D] rounded-xl p-8 text-white shadow-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-xs tracking-widest opacity-80">
                              GIFT CARD
                            </span>
                            <span className="text-xs opacity-80">
                              {businessInfo?.name || ""}
                            </span>
                          </div>

                          <div className="mt-8">
                            <p className="text-3xl font-semibold">
                              {formatPrice(
                                Number(giftCardAmountDraft || 0) || 0,
                                currency,
                              )}
                            </p>
                          </div>

                          <div className="mt-10">
                            <p className="text-[10px] tracking-widest opacity-70">
                              MESSAGE
                            </p>
                            <p className="mt-2 text-sm opacity-90 line-clamp-2">
                              {giftCardMessageDraft?.trim()
                                ? giftCardMessageDraft
                                : "Enter up to 15 words"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </section>

              <section
                className="space-y-4 mb-10"
                id="promos"
                style={{ scrollMarginTop: `${headerHeight + 40}px` }}
              >
                {promos && promos.length > 0 && (
                  <>
                    <div className=" mt-10 mb-4">
                      <h2 className="text-lg font-semibold text-grey-900 mb-2 font-heading">
                        Special Offers
                      </h2>
                      <p className="text-sm text-grey-600">
                        Limited-time promotions and discounts on our services
                      </p>
                    </div>

                    {promos.map((promo: any) => (
                      <div
                        key={promo.id}
                        onClick={() => setPromoCode(promo.code)}
                        className="bg-white rounded-lg p-6 border-2 border-green-100 cursor-pointer"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Tag className="h-5 w-5 text-green-300" />
                              <h3 className="text-xl font-semibold text-grey-900 font-heading">
                                {promo.title || promo.name}
                              </h3>
                            </div>
                            <p className="text-grey-600 leading-relaxed mb-3">
                              {promo.description}
                            </p>

                            {/* Validity Period */}
                            {(promo.start_date || promo.end_date) && (
                              <div className="mt-3 text-sm text-grey-500">
                                {promo.end_date && (
                                  <p>
                                    Valid until{" "}
                                    {new Date(
                                      promo.end_date,
                                    ).toLocaleDateString("en-US", {
                                      month: "long",
                                      day: "numeric",
                                      year: "numeric",
                                    })}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Promo Code if available */}
                            {promo.code && (
                              <div className="mt-4 p-3 bg-grey-50 rounded-lg">
                                <p className="text-xs text-grey-600 mb-1">
                                  Use promo code:
                                </p>
                                <p className="text-lg font-mono font-bold text-grey-900">
                                  {promo.code}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Terms and Conditions if available */}
                        {promo.terms && (
                          <div className="mt-4 pt-4 border-t border-grey-100">
                            <p className="text-xs text-grey-500">
                              <span className="font-medium">
                                Terms & Conditions:
                              </span>{" "}
                              {promo.terms}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </section>

              <section
                id="reviews"
                className="space-y-4"
                style={{ scrollMarginTop: `${headerHeight + 40}px` }}
              >
                {isBookingLinkActive && (
                  <div className="bg-white rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-grey-900 mb-4 font-heading">
                      Reviews
                    </h2>
                    {hasReviews ? (
                      <>
                        <div className="flex items-center gap-4 mb-6">
                          <div>
                            <div className="flex items-center gap-2">
                              <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                              <span className="text-3xl font-semibold tracking-tight">
                                {averageReviewRating.toFixed(1)}
                              </span>
                            </div>
                            <p className="text-grey-600 text-sm">
                              Based on {reviewList.length} review
                              {reviewList.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          {displayedReviews.map((review: any) => {
                            const stars = review.stars ?? review.rating ?? 0;
                            const message =
                              review.message ?? review.comment ?? "";
                            const clientName =
                              review.client?.name ??
                              review.client_name ??
                              "Anonymous";
                            const replies = getReviewReplies(review);

                            return (
                              <div
                                key={review.id}
                                className="border-b border-grey-100 pb-4 last:border-b-0 last:pb-0"
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="flex">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                      <Star
                                        key={i}
                                        className={`h-4 w-4 ${
                                          i <= stars
                                            ? "fill-yellow-400 text-yellow-400"
                                            : "text-grey-300"
                                        }`}
                                      />
                                    ))}
                                  </div>
                                  <span className="font-semibold text-grey-900">
                                    {clientName}
                                  </span>
                                  {review.created_at && (
                                    <span className="text-grey-500 text-sm">
                                      •{" "}
                                      {new Date(
                                        review.created_at,
                                      ).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                {message && (
                                  <p className="text-grey-700 leading-relaxed">
                                    {message}
                                  </p>
                                )}
                                {replies.length > 0 && (
                                  <div className="mt-3 ml-6 space-y-3 border-l-2 border-grey-100 pl-4">
                                    {replies.map((reply: any) => (
                                      <div
                                        key={reply.id}
                                        className="rounded-md bg-grey-50 px-4 py-3"
                                      >
                                        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                                          <span className="font-semibold text-grey-900">
                                            {getReplyAuthorLabel(reply)}
                                          </span>
                                          {reply.created_at && (
                                            <span className="text-grey-500">
                                              •{" "}
                                              {new Date(
                                                reply.created_at,
                                              ).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-sm leading-relaxed text-grey-700">
                                          {reply.body}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {!showAllReviews && hasMoreReviewsThanInitial && (
                          <div className="mt-6">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowAllReviews(true);
                                setReviewsPage(1);
                              }}
                            >
                              Show all reviews
                            </Button>
                          </div>
                        )}

                        {showAllReviews && totalReviewPages > 1 && (
                          <div className="mt-6 flex items-center justify-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setReviewsPage((page) => Math.max(1, page - 1))
                              }
                              disabled={reviewsPage === 1}
                            >
                              Previous
                            </Button>
                            <span className="text-sm text-grey-600">
                              Page {reviewsPage} of {totalReviewPages}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setReviewsPage((page) =>
                                  Math.min(totalReviewPages, page + 1),
                                )
                              }
                              disabled={reviewsPage === totalReviewPages}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-grey-600 text-sm">No reviews yet.</p>
                    )}
                  </div>
                )}
              </section>

              {faqs && faqs.length > 0 && (
                <section
                  id="faqs"
                  className="space-y-4 mt-10 mx-auto"
                  style={{ scrollMarginTop: `${headerHeight + 40}px` }}
                >
                  <div className="">
                    <h2 className="text-xl font-semibold text-grey-900 mb-2 font-heading text-center">
                      Frequently Asked Questions (FAQs)
                    </h2>
                    <p className="text-grey-600 text-sm mb-6 text-center">
                      Get meticulously curated responses to frequently asked
                      questions.
                    </p>
                    <div className="space-y-0 bg-white rounded-lg p-6 shadow-[3px_3px_3px_0px_rgba(0,0,0,0.02)]">
                      {faqs.map((faq: any, index: number) => {
                        const isExpanded = expandedFaqs.includes(
                          faq.id || index.toString(),
                        );
                        return (
                          <div
                            key={faq.id || index}
                            className={`border-b border-grey-100 last:border-b-0 overflow-hidden transition-all duration-300 ${
                              isExpanded ? "bg-grey-5" : ""
                            }`}
                          >
                            <button
                              onClick={() =>
                                toggleFaq(faq.id || index.toString())
                              }
                              className="w-full py-4 px-2 flex items-start justify-between gap-4 hover:bg-grey-10 transition-colors"
                            >
                              <h3 className="text-base font-semibold text-grey-900 text-left flex-1">
                                {faq.question}
                              </h3>
                              <div className="flex-shrink-0 w-6 flex items-center justify-center">
                                {isExpanded ? (
                                  <span className="text-grey-600 text-2xl font-light">
                                    −
                                  </span>
                                ) : (
                                  <span className="text-grey-600 text-2xl font-light">
                                    +
                                  </span>
                                )}
                              </div>
                            </button>
                            <div
                              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                                isExpanded
                                  ? "max-h-[1000px] opacity-100"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="px-2 pb-4 pt-0 text-grey-700 leading-relaxed animate-in slide-in-from-top-2 duration-300">
                                {faq.answer}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>
              )}

              <section
                id="about"
                className="space-y-4 mt-10"
                style={{ scrollMarginTop: `${headerHeight + 40}px` }}
              >
                {businessInfo?.about && (
                  <div className="bg-white rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-grey-900 mb-3 font-heading">
                      About
                    </h2>
                    <p className="text-grey-700 leading-relaxed">
                      {businessInfo.about}
                    </p>
                  </div>
                )}

                {/* Map Section */}
                <div className="bg-white rounded-lg p-6">
                  <div className="relative rounded-lg overflow-hidden h-64">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                        `${selectedSalon?.address || ""} ${
                          selectedSalon?.city || ""
                        } ${selectedSalon?.state || ""}`,
                      )}&zoom=15`}
                      allowFullScreen
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-sm text-grey-600">
                      {selectedSalon?.address}
                      {(selectedSalon?.city || selectedSalon?.state) && (
                        <span>
                          , {selectedSalon?.city}
                          {selectedSalon?.state && `, ${selectedSalon.state}`}
                        </span>
                      )}
                    </div>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        `${selectedSalon?.address} ${selectedSalon?.city} ${selectedSalon?.state}`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Get directions
                    </a>
                  </div>
                </div>

                {/* Opening Times and Additional Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Opening Times */}
                  {selectedSalon?.salonHours && (
                    <div className="bg-white rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-grey-900 mb-4 font-heading">
                        Opening times
                      </h3>
                      <div className="space-y-3">
                        {groupWorkingHoursByDay(selectedSalon.salonHours).map(
                          (day, index) => {
                            const dayName = day.day.toLowerCase();
                            const salonTimezone = selectedSalon.timezone;
                            const salonToday = parseDateOnly(
                              dateOnlyFromInstant(new Date(), salonTimezone),
                            )!;
                            const today =
                              weekdayForDateOnly(salonToday).toLowerCase();
                            const isToday = dayName === today;
                            return (
                              <div
                                key={index}
                                className="flex items-center justify-between"
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-2 h-2 rounded-full ${
                                      day.times.length > 0
                                        ? "bg-green-500"
                                        : "bg-grey-300"
                                    }`}
                                  />
                                  <span
                                    className={`capitalize ${
                                      isToday
                                        ? "font-medium text-grey-900"
                                        : "text-grey-700"
                                    }`}
                                  >
                                    {day.day}
                                  </span>
                                </div>
                                <div className="text-right">
                                  {day.times.length > 0 ? (
                                    <div className="space-y-1">
                                      {day.times.map((slot, slotIndex) => (
                                        <div
                                          key={slotIndex}
                                          className="text-grey-700 text-sm"
                                        >
                                          {formatTime(slot.openTime)} -{" "}
                                          {formatTime(slot.closeTime)}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-grey-500">
                                      Closed
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Information */}
                  <div className="bg-white rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-grey-900 mb-4 font-heading">
                      Additional information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <svg
                          className="w-5 h-5 text-grey-400 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-grey-700">
                          Instant Confirmation
                        </span>
                      </div>

                      {/* Specialties if they exist */}
                      {businessInfo?.businessSpecialties &&
                        businessInfo.businessSpecialties.length > 0 && (
                          <>
                            <div className="pt-2 border-t border-grey-100">
                              <p className="text-sm font-medium text-grey-900 mb-2">
                                Specialties
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {businessInfo.businessSpecialties.map(
                                  (specialty, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-1 bg-grey-50 text-grey-700 rounded text-xs"
                                    >
                                      {toSentenceCase(specialty)}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Desktop Cart Sidebar */}
            <div className="hidden lg:block w-96 min-h-0">
              <div
                className="sticky min-h-0 flex flex-col overflow-hidden"
                style={{
                  top: stickyChromeHeight + 16,
                  maxHeight: `calc(100dvh - ${stickyChromeHeight + 32}px)`,
                }}
              >
                <CartSummary
                  layoutKey={`${singleHeaderImageValid}-${stickyChromeHeight}`}
                  showButton={true}
                  customButton={{
                    text:
                      isVoucherPurchase ||
                      isGiftCardPurchase ||
                      isProductOnlyPurchase
                        ? "Checkout"
                        : "Continue to booking",
                    onClick: () => {
                      if (isVoucherPurchase) {
                        handleNextStep(BookingStep.VOUCHER_CHECKOUT);
                        return;
                      }
                      if (isGiftCardPurchase) {
                        handleNextStep(BookingStep.GIFT_CARD_CHECKOUT);
                        return;
                      }
                      if (isProductOnlyPurchase) {
                        tryStartProductCheckout();
                        return;
                      }
                      if (businessInfo?.allowGroupBooking) {
                        ensureMeGuest();
                        setActiveGuestId(null);
                        handleNextStep(BookingStep.GUESTS_AND_SERVICES);
                        return;
                      }
                      const nextStep = businessInfo?.allowServiceStaffSelection
                        ? BookingStep.STAFF_SELECTION
                        : BookingStep.TIME_AND_DATE;
                      handleNextStep(nextStep);
                    },
                    className: "w-full bg-green-300 text-white",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Sticky Cart Button */}
        {cartCount > 0 && isMobile && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-grey-100 lg:hidden">
            <Button
              onClick={() => {
                if (isVoucherPurchase) {
                  handleNextStep(BookingStep.VOUCHER_CHECKOUT);
                  return;
                }
                if (isGiftCardPurchase) {
                  handleNextStep(BookingStep.GIFT_CARD_CHECKOUT);
                  return;
                }
                if (isProductOnlyPurchase) {
                  tryStartProductCheckout();
                  return;
                }
                if (businessInfo?.allowGroupBooking) {
                  ensureMeGuest();
                  setActiveGuestId(null);
                  handleNextStep(BookingStep.GUESTS_AND_SERVICES);
                  return;
                }
                const nextStep = businessInfo?.allowServiceStaffSelection
                  ? BookingStep.STAFF_SELECTION
                  : BookingStep.TIME_AND_DATE;
                handleNextStep(nextStep);
              }}
              className="w-full bg-green-300   text-white h-12"
            >
              {isVoucherPurchase || isGiftCardPurchase || isProductOnlyPurchase
                ? "Checkout"
                : "Continue"}{" "}
              • {cartCount} {cartCount === 1 ? "item" : "items"}
            </Button>
          </div>
        )}

        {/* Voucher Redemption Modal */}
        <VoucherRedemptionModal />

        {/* Cart Dialog */}
        <CartDialog open={isCartOpen} onOpenChange={setIsCartOpen} />
      </div>
    </>
  );
}

function useOverflowFadeMask(ref: RefObject<HTMLElement | null>) {
  const [maskImage, setMaskImage] = useState<string | undefined>();

  const updateFade = () => {
    const el = ref.current;
    if (!el) return;

    const fadeLeft = el.scrollLeft > 2;
    const fadeRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    if (!fadeLeft && !fadeRight) {
      setMaskImage(undefined);
      return;
    }

    const start = fadeLeft ? "transparent" : "#000";
    const end = fadeRight ? "transparent" : "#000";
    setMaskImage(
      `linear-gradient(to right, ${start} 0, #000 2.5rem, #000 calc(100% - 2.5rem), ${end} 100%)`,
    );
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    updateFade();
    const resizeObserver = new ResizeObserver(updateFade);
    resizeObserver.observe(el);
    const mutationObserver = new MutationObserver(updateFade);
    mutationObserver.observe(el, { childList: true, subtree: true });
    window.addEventListener("resize", updateFade);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", updateFade);
    };
  }, [ref]);

  return { maskImage, onScroll: updateFade };
}

function HorizontalFadeScroller({
  className,
  children,
  role,
  ariaLabel,
  scrollerRef,
}: {
  className?: string;
  children: ReactNode;
  role?: string;
  ariaLabel?: string;
  scrollerRef?: RefObject<HTMLDivElement | null>;
}) {
  const localRef = useRef<HTMLDivElement | null>(null);
  const ref = scrollerRef ?? localRef;
  const { maskImage, onScroll } = useOverflowFadeMask(ref);

  return (
    <div
      ref={ref as RefObject<HTMLDivElement>}
      role={role}
      aria-label={ariaLabel}
      onScroll={onScroll}
      className={className}
      style={maskImage ? { maskImage, WebkitMaskImage: maskImage } : undefined}
    >
      {children}
    </div>
  );
}

function NavigationTab({
  activeSection,
  label,
  onClick,
}: {
  activeSection: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`cursor-pointer px-4 py-2 rounded-full whitespace-nowrap transition-all text-sm font-medium ${
        activeSection === label.toLowerCase()
          ? "bg-green-300 text-white"
          : "bg-grey-10 text-black  "
      }`}
    >
      {label}
    </button>
  );
}

function ServiceCategoryPills({
  categories,
  selectedId,
  onSelect,
  ariaLabel = "Service categories",
}: {
  categories: { name: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
  ariaLabel?: string;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const pill = selectedRef.current;
    const row = rowRef.current;
    if (!pill || !row) return;

    const target = pill.offsetLeft - row.clientWidth / 2 + pill.offsetWidth / 2;
    row.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [selectedId]);

  const pills = [
    { id: ALL_SERVICES_CATEGORY, label: "All" },
    ...categories.map((category) => ({
      id: category.name,
      label: category.name,
    })),
  ];

  return (
    <HorizontalFadeScroller
      scrollerRef={rowRef}
      role="tablist"
      ariaLabel={ariaLabel}
      className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 w-full min-w-0"
    >
      {pills.map((pill) => {
        const isSelected = selectedId === pill.id;
        return (
          <button
            key={pill.id}
            ref={isSelected ? selectedRef : undefined}
            type="button"
            role="tab"
            aria-selected={isSelected}
            onClick={() => onSelect(pill.id)}
            className={`cursor-pointer shrink-0 px-4 py-2 rounded-full whitespace-nowrap uppercase text-xs font-semibold tracking-wide transition-all border ${
              isSelected
                ? "bg-green-300 text-white border-green-300"
                : "bg-white text-grey-900 border-grey-100"
            }`}
          >
            {pill.label}
          </button>
        );
      })}
    </HorizontalFadeScroller>
  );
}
